import React, { useEffect, useState } from 'react';
import { Loader2, Clock, Check, RotateCcw } from 'lucide-react';
import { Button } from '../components/ui/button';
import {
  SimpleStory,
  getPendingReviewStories,
  getProfilesByIds,
} from '../services/simpleStore';
import { STORY_STATUS_COLORS } from '../types';

interface EditorDeskProps {
  onEditStory: (storyId: string) => void;
}

const LANGUAGE_DISPLAY: Record<string, string> = {
  'fa-AF': 'Dari',
  'ps-AF': 'Pashto',
  'eng': 'English',
};

function timeAgo(dateStr: string): string {
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return `${days}d ago`;
}

const EditorDesk: React.FC<EditorDeskProps> = ({ onEditStory }) => {
  const [stories, setStories] = useState<SimpleStory[]>([]);
  const [loading, setLoading] = useState(true);
  const [authorNames, setAuthorNames] = useState<Map<string, { fullName: string; email: string }>>(new Map());

  const loadStories = async () => {
    setLoading(true);
    const pending = await getPendingReviewStories();
    setStories(pending);

    // Load author names in batch
    const authorIds = [...new Set(pending.map(s => s.authorId).filter(Boolean))];
    if (authorIds.length > 0) {
      const profiles = await getProfilesByIds(authorIds);
      setAuthorNames(profiles);
    }

    setLoading(false);
  };

  useEffect(() => { loadStories(); }, []);

  return (
    <div className="h-full bg-zinc-50 dark:bg-zinc-950 animate-fade-in">
      {/* Header */}
      <div className="bg-white dark:bg-zinc-900 border-b border-zinc-200 dark:border-zinc-800 px-6 py-4">
        <div className="max-w-5xl mx-auto flex items-center justify-between">
          <div>
            <h1 className="text-xl font-bold text-zinc-900 dark:text-white">
              Editor Desk
            </h1>
            <p className="text-sm text-zinc-500 dark:text-zinc-400 mt-1">
              {loading ? 'Loading...' : `${stories.length} ${stories.length === 1 ? 'story' : 'stories'} awaiting review`}
            </p>
          </div>
          <Button onClick={loadStories} variant="outline" size="sm" disabled={loading}>
            <RotateCcw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-5xl mx-auto p-6">
        {loading && stories.length === 0 ? (
          <div className="flex justify-center py-20">
            <Loader2 className="h-8 w-8 animate-spin text-blue-500" />
          </div>
        ) : stories.length === 0 ? (
          <div className="text-center py-20 bg-white dark:bg-zinc-900 rounded-xl border border-dashed border-zinc-300 dark:border-zinc-700">
            <div className="bg-green-50 dark:bg-green-900/20 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
              <Check className="h-8 w-8 text-green-500 dark:text-green-400" />
            </div>
            <h3 className="text-lg font-medium text-zinc-900 dark:text-white">
              All clear
            </h3>
            <p className="text-zinc-500 dark:text-zinc-400 mt-1 max-w-sm mx-auto">
              No stories are waiting for review. Check back later.
            </p>
          </div>
        ) : (
          <div className="space-y-3">
            {stories.map((story) => {
              const author = authorNames.get(story.authorId);
              const isRtl = story.language !== 'eng';
              const title = story.platformMetadata?.slug || story.headline || 'Untitled';

              return (
                <button
                  key={story.id}
                  onClick={() => onEditStory(story.id)}
                  className="w-full text-left p-4 bg-white dark:bg-zinc-900 rounded-lg border border-zinc-200 dark:border-zinc-800 hover:border-blue-300 dark:hover:border-blue-700 hover:shadow-sm transition-all group"
                >
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1 min-w-0">
                      <h3
                        className="font-medium text-zinc-900 dark:text-white truncate group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors"
                        dir={isRtl ? 'rtl' : 'ltr'}
                      >
                        {title}
                      </h3>
                      <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mt-1.5 text-xs text-zinc-500 dark:text-zinc-400">
                        <span className="font-medium">
                          {LANGUAGE_DISPLAY[story.language] || story.language}
                        </span>
                        <span className="text-zinc-300 dark:text-zinc-600">|</span>
                        <span>{story.platform === 'tv' ? 'TV' : 'Web'}</span>
                        {author && (
                          <>
                            <span className="text-zinc-300 dark:text-zinc-600">|</span>
                            <span>By {author.fullName || author.email}</span>
                          </>
                        )}
                        {story.submittedForReviewAt && (
                          <>
                            <span className="text-zinc-300 dark:text-zinc-600">|</span>
                            <span className="flex items-center gap-1">
                              <Clock className="h-3 w-3" />
                              {timeAgo(story.submittedForReviewAt)}
                            </span>
                          </>
                        )}
                      </div>
                    </div>
                    <span className={`px-2.5 py-1 text-xs font-medium rounded-full whitespace-nowrap ${STORY_STATUS_COLORS['pending_review']}`}>
                      Pending Review
                    </span>
                  </div>
                </button>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default EditorDesk;
